import React from "react";

import { Box, withStyles } from "@material-ui/core";
import { connect } from "react-redux";
import { Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import BackgroundSlider from "components/BackgroundSlider/BackgroundSlider";
import CustomDialogWarning from "components/Dialog/CustomDialogWarning";
import NotificationsContainer from "containers/notifications/NotificationsContainer";
import { lastSuccessNotificationSelector } from "containers/notifications/selectors";
import { formatString } from "lang/utils";
import TLang from "model/application/Lang";
import IRootState from "redux/store/model";

import SnackbarMessage from "../../../components/Snackbars/SnackbarMessage";
import * as lang from "../../../lang";
import PasswordLostModal from "../components/modals/PasswordLostModal";
import LoginErrorBoundary from "../LoginErrorBoundary";
import LoginForm from "../LoginForm";
import { changeLanguage } from "../redux/actions";
import * as actions from "../redux/actions";
import {
  authErrorsSelector,
  authStatusSelector,
  errorMessageSelector,
  getIsPasswordRecovering,
  getLang,
  getRetryWait,
  hasLoginErrorSelector,
} from "../redux/selector";
import styles from "../styles";

interface ILoginContainerProps extends RouteComponentProps {
  actions: any; // TODO type actions
  classes: any;
  retryWait: boolean;
  isAuthenticated: boolean;
  error: any;
  hasLoginError: boolean;
  errorMessage?: string;
  isPasswordRecovering: boolean;
  lastSuccessNotification: number;
  locale: string;
  changeLanguage: (lang: string) => void;
  resetRetryWait: () => void;
}

interface ILoginContainerState {
  isLoggingIn: boolean;
  retryWait: boolean;
  redirectToReferrer: boolean;
  error: any;
  errorMessage: string;
  isPasswordLostModalOpen: boolean;
  currLang: TLang;
  hasLoginError: boolean;
  lastSuccessNotification: number;
  successModalOpen: boolean;
  email: string;
}

/**
 * LoginContainer container component
 * export keyword here is used to import this module as a named import
 * useful when running tests
 */
export class LoginContainerRaw extends React.Component<
  ILoginContainerProps,
  ILoginContainerState
> {
  constructor(props: ILoginContainerProps) {
    super(props);

    this.state = {
      isLoggingIn: false,
      retryWait: false,
      redirectToReferrer: false,
      error: undefined,
      errorMessage: "",
      isPasswordLostModalOpen: false,
      hasLoginError: false,
      lastSuccessNotification: -1,
      successModalOpen: false,
      email: "",
      // @ts-expect-error TS7053
      currLang: lang[props.locale],
    };

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleGoogleLogin = this.handleGoogleLogin.bind(this);
  }
  logger = this.context;

  /**
   * handle Form submission
   *
   * */

  async handleFormSubmit(email: string, password: string) {
    try {
      this.setState({ isLoggingIn: true });
      await this.props.actions.loginAction(email, password);
      this.setState({ isLoggingIn: false });
    } catch (e) {
      this.logger.logError(e);
      this.setState({ isLoggingIn: false });
    }
  }

  handleLangChange = (lang: string) => {
    const { changeLanguage } = this.props;
    changeLanguage(lang.toLowerCase());
  };

  /**
   * handle Google Login
   * */
  async handleGoogleLogin(googleData: any) {
    if (googleData.tokenId) {
      try {
        this.setState({ isLoggingIn: true });
        await this.props.actions.googleLoginAction(googleData.tokenId);
        this.setState({ isLoggingIn: false });
      } catch (e) {
        this.logger.logError(e);
        this.setState({ isLoggingIn: false });
      }
    }
  }

  /**
   * Handles the closing and opening of the password lost modal
   */
  handleOpenClosePasswordLost = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        isPasswordLostModalOpen: !prevState.isPasswordLostModalOpen,
      };
    });
  };

  /**
   * Handles recover password
   */
  handleRecoverPassword = (email: string, token: string) => {
    this.props.actions.recoverPasswordAction(email, token);
    this.setState({ email });
  };

  /**
   * Make any necessary updates when we receive props. These props will ideally come from the Redux Store
   * @param {Object} nextProps Next Properties received from the Redux Store state using the mapStateToProps function
   * @param {Object} prevState Previous State of the component
   * TODO: there should be another cleaner way to do this (using a redux middleware), but we can stick to it for now.
   * This method is meant to control the closing of the Edit/Create modals when the backend is sending a SUCCESS answer
   * */
  static getDerivedStateFromProps(
    nextProps: ILoginContainerProps,
    prevState: ILoginContainerState,
  ) {
    const {
      lastSuccessNotification,
      retryWait,
      hasLoginError,
      error,
      isAuthenticated,
      errorMessage,
    } = nextProps;
    if (
      lastSuccessNotification !== prevState.lastSuccessNotification ||
      retryWait !== prevState.retryWait ||
      hasLoginError !== prevState.hasLoginError ||
      error !== prevState.error ||
      isAuthenticated !== prevState.redirectToReferrer ||
      errorMessage !== prevState.errorMessage
    ) {
      return {
        hasLoginError,
        retryWait,
        error,
        isAuthenticated,
        errorMessage,
        lastSuccessNotification,
        successModalOpen: Date.now() - lastSuccessNotification < 1000,
      };
    } else {
      return null;
    }
  }

  /**
   * Render container component
   * This will render the Login page or redirect if the user has already been registered with the store
   * the from object will take the previous location before the redirect happened and pass that to redirect
   * This is useful for returning the user back to the location they were in before the redirect to login.
   */
  render() {
    const { from } = (this.props.location.state as any) || {
      from: { pathname: "/" },
    };
    const {
      redirectToReferrer,
      isLoggingIn,
      retryWait,
      errorMessage,
      isPasswordLostModalOpen,
      successModalOpen,
      email,
      currLang,
    } = this.state;

    const { hasLoginError, isPasswordRecovering, classes, locale } = this.props;

    // whether to redirect to referrer, when the user is successfully logged in
    if (redirectToReferrer) {
      return <Redirect to={from} />;
    }

    return (
      <LoginErrorBoundary client={undefined} user={undefined}>
        <Box className={classes.formContainer}>
          <Box className={classes.loginFormContainer}>
            <LoginForm
              key={`${locale}-${retryWait}`}
              onFormSubmit={this.handleFormSubmit}
              selectedLanguage={locale ?? "en"}
              handleLangChange={this.handleLangChange}
              onGoogleLogin={this.handleGoogleLogin}
              retryWait={retryWait}
              isLoggingIn={isLoggingIn}
              displayError={hasLoginError}
              errorMessage={errorMessage}
              onOpenClosePasswordLost={this.handleOpenClosePasswordLost}
            />
          </Box>
          <BackgroundSlider />
        </Box>
        <PasswordLostModal
          isOpen={isPasswordLostModalOpen}
          onOpenClose={this.handleOpenClosePasswordLost}
          onRecoverPassword={this.handleRecoverPassword}
          lang={currLang}
        />
        <CustomDialogWarning
          isOpen={successModalOpen}
          onClose={() => this.setState({ successModalOpen: false })}
          rootLang={currLang}
          lang={{
            ...currLang.containers.login.subCategories.login
              .passwordLostMessageModal,
            description: formatString(
              currLang.containers.login.subCategories.login
                .passwordLostMessageModal.description,
              [email],
            ),
          }}
        />
        <NotificationsContainer lang={currLang} />
        <SnackbarMessage
          isRecoveringPasswordActionOngoing={isPasswordRecovering}
          lang={currLang}
        />
      </LoginErrorBoundary>
    );
  }
}

/**
 * maps the state of the redux store to the LoginContainer props
 * @param {Object} state of redux store
 * @param {Object} ownProps LoginContainer properties
 * @returns {Object} new state of redux store
 */
function mapStateToProps(state: IRootState) {
  return {
    retryWait: getRetryWait(state),
    error: authErrorsSelector(state),
    isAuthenticated: authStatusSelector(state),
    hasLoginError: hasLoginErrorSelector(state),
    errorMessage: errorMessageSelector(state),
    isPasswordRecovering: getIsPasswordRecovering(state),
    lastSuccessNotification: lastSuccessNotificationSelector(state),
    locale: getLang(state),
  };
}

/**
 * maps dispatch actions to props in this container
 * component
 * @param {Object} dispatch
 * @returns {Object} actions object
 */
function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch),
    changeLanguage: bindActionCreators(changeLanguage, dispatch),
    resetRetryWait: bindActionCreators(actions.resetRetryWait, dispatch),
  };
}

/**
 * Connect LoginContainer container to redux store and map
 * actions to the store and props of this container to
 * state of store
 */
export default withStyles(styles)(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(LoginContainerRaw)),
);
